/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components
import { Link, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

function Breadcrumbs({ icon, title, route, light }) {
  const navigate = useNavigate();
  
  // Route mapping for proper navigation
  const routeMap = {
    "dashboard": { label: "Dashboard", path: "/dashboard" },
    "notis-tunggakan": { label: "Notis Tunggakan", path: "/notis-tunggakan" },
    "notis": { label: "Notis", path: null }, // Parent only
    "tunggakan": { label: "Tunggakan", path: "/notis/tunggakan" },
    "inbois-sewaan": { label: "Inbois Sewaan", path: null }, // Parent only
    "auto-jana-inbois": { label: "Auto Jana Inbois", path: "/inbois-sewaan/auto-jana-inbois" },
    "jana-inbois": { label: "Jana Inbois", path: "/inbois-sewaan/jana-inbois" },
    "laporan": { label: "Laporan", path: "/laporan/laporan" },
    "terimaan": { label: "Terimaan", path: "/laporan/terimaan" },
    "pelanggan": { label: "Pelanggan", path: null }, // Parent only
    "maklumat-pelanggan": { label: "Maklumat Pelanggan", path: "/pelanggan/maklumat-pelanggan" },
    "senggara": { label: "Senggara", path: null }, // Parent only
    "senarai-penyewa": { label: "Senarai Penyewa", path: "/senggara/senarai-penyewa" },
    "senarai-harta": { label: "Senarai Harta", path: "/senggara/senarai-harta" },
    "senarai-zon-dan-kategori-kutipan": { label: "Zon & Kategori", path: "/senggara/senarai-zon-dan-kategori-kutipan" },
    "admin": { label: "Admin", path: "/admin" },
    "nama": { label: "Nama", path: "/admin/nama" },
    "akaun": { label: "Akaun", path: "/admin/akaun" },
    "harta": { label: "Harta", path: "/admin/harta" },
    "kategori-kutipan": { label: "Kategori Kutipan", path: "/admin/kategori-kutipan" },
    "zon": { label: "Zon", path: "/admin/zon" },
    "inbois": { label: "Inbois", path: "/admin/inbois" },
    "lejar": { label: "Lejar", path: "/admin/lejar" },
    "deposit": { label: "Deposit", path: "/admin/deposit" },
    "sessions": { label: "Sessions", path: "/admin/sessions" },
    "qr": { label: "QR", path: null }, // No navigation
    "qr-preview": { label: "QR Preview", path: null },
    "authentication": { label: "Authentication", path: null },
    "sign-in": { label: "Sign In", path: null },
  };
  
  // Smart breadcrumb generation based on route patterns
  const generateBreadcrumbs = () => {
    const breadcrumbs = [];
    
    // Special patterns that need custom handling
    
    // Pattern: /profile/customer/:idNama
    if (route.length >= 3 && route[0] === "profile" && route[1] === "customer") {
      breadcrumbs.push({
        label: "Maklumat Pelanggan",
        path: "/pelanggan/maklumat-pelanggan",
        clickable: true
      });
      breadcrumbs.push({
        label: route[2], // idNama
        path: null,
        clickable: false
      });
      return breadcrumbs;
    }
    
    // Pattern: /harta/:kodHarta or /harta/:kodHarta/:noAkaun
    if (route.length >= 2 && route[0] === "harta") {
      breadcrumbs.push({
        label: "Maklumat Pelanggan",
        path: "/pelanggan/maklumat-pelanggan",
        clickable: true
      });
      breadcrumbs.push({
        label: route[1], // kodHarta
        path: null,
        clickable: false
      });
      if (route.length >= 3) {
        // Has noAkaun parameter
        breadcrumbs.push({
          label: route[2], // noAkaun
          path: null,
          clickable: false
        });
      }
      return breadcrumbs;
    }
    
    // Pattern: /terimaan/terimaan-hasil (special case)
    if (route.length === 2 && route[0] === "terimaan" && route[1] === "terimaan-hasil") {
      breadcrumbs.push({
        label: "Terimaan Hasil",
        path: "/terimaan/terimaan-hasil",
        clickable: false
      });
      return breadcrumbs;
    }
    
    // Default behavior: Use routeMap for known routes
    for (let i = 0; i < route.length - 1; i++) {
      const segment = route[i];
      const mapping = routeMap[segment];
      
      if (mapping) {
        breadcrumbs.push({
          label: mapping.label,
          path: mapping.path,
          clickable: !!mapping.path
        });
      } else {
        // Unknown segment - try incremental path (but might be 404)
        const incrementalPath = `/${route.slice(0, i + 1).join("/")}`;
        breadcrumbs.push({
          label: segment,
          path: incrementalPath,
          clickable: false // Don't make clickable if unknown
        });
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  
  // Format display text
  const formatLabel = (label) => {
    // Check if label looks like IC number (digits with dashes), if so remove dashes
    if (/^\d{6}-\d{2}-\d{4}$/.test(label)) {
      return label.replace(/[-\s]/g, "");
    }
    // Otherwise replace dashes with spaces and capitalize
    return label.replace(/-/g, " ");
  };

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
          },
        }}
      >
        {/* Home icon */}
        <Link to="/">
          <MDTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>
        
        {/* Generated breadcrumbs */}
        {breadcrumbs.map((crumb, index) => {
          if (crumb.clickable && crumb.path) {
            return (
              <span
                key={index}
                onClick={() => navigate(crumb.path)}
                style={{ cursor: 'pointer' }}
              >
                <MDTypography
                  component="span"
                  variant="button"
                  fontWeight="regular"
                  textTransform="capitalize"
                  sx={{ lineHeight: 0, color: light ? "white" : "#0a1628", opacity: 0.6 }}
                >
                  {formatLabel(crumb.label)}
                </MDTypography>
              </span>
            );
          } else {
            return (
              <MDTypography
                key={index}
                variant="button"
                fontWeight="regular"
                textTransform="capitalize"
                sx={{ lineHeight: 0, color: light ? "white" : "#0a1628", opacity: 0.6 }}
              >
                {formatLabel(crumb.label)}
              </MDTypography>
            );
          }
        })}
        
        {/* Current page (title) - not clickable */}
        <MDTypography
          variant="button"
          fontWeight="regular"
          textTransform="capitalize"
          sx={{ lineHeight: 0, color: light ? "white" : "#0a1628", opacity: 0.8 }}
        >
          {formatLabel(title)}
        </MDTypography>
      </MuiBreadcrumbs>
      
      {/* Page title below breadcrumb */}
      <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        sx={{ color: light ? "white" : "#0a1628" }}
        noWrap
      >
        {formatLabel(title)}
      </MDTypography>
    </MDBox>
  );
}

// Setting default values for the props of Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false,
};

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool,
};

export default Breadcrumbs;
